import React, { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BackArrow, Button, InfoBlock, TopProgress } from '../../../components'
import ReleaseDedicatedPreorder from '../../../components/modal-alerts/release-dedicated-preorder'
import dedicatedPreordersService from '../../../api/dedicatedPreordersService'
import { usePageTitle } from '../../../context/navigationContext'
import { useUi } from '../../../context/ui.context'
import { formatDate } from '../../../utils/date-formatter'
import { DetailContainer, DetailGrid, DetailHeader, DetailTitle } from '../styles'
import { ErrorMessage, FormActions } from '../preorder-hardware/styles'
import { canReleasePreorder, diskDescriptions, preorderError, preordersPath, preorderStatusKey } from './helpers'
import { Configuration, Note, PreorderBadge, ResourceLink } from './styles'
import usePolledData from './usePolledData'

const Details: FC<{ id: string }> = ({ id }) => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  const [releaseOpen, setReleaseOpen] = useState(false)
  const [operationId, setOperationId] = useState('')
  const request = useCallback((signal: AbortSignal) => dedicatedPreordersService.get(id, signal), [id])
  const { data: preorder, loading, error, reload } = usePolledData(request)
  const title = preorder?.name || String(t('PREORDERS_DETAILS'))
  usePageTitle(title)

  return (
    <DetailContainer data-testid="dedicated-preorder-details">
      <BackArrow fallbackPath={preordersPath} fallbackTitle={String(t('PREORDERS_TITLE'))} />
      <DetailHeader>
        <DetailTitle>{title}</DetailTitle>
        {preorder && <PreorderBadge $status={preorder.status}>{t(preorderStatusKey(preorder.status))}</PreorderBadge>}
      </DetailHeader>
      {loading ? <TopProgress loading /> : !preorder ? (
        <ErrorMessage role="alert">{preorderError(error, String(t('PREORDERS_LOAD_FAILED')))}</ErrorMessage>
      ) : <>
        <FormActions style={{ marginTop: 0, marginBottom: 20 }}>
          <Button variant="stroke" size="display" title={t('EDIT')} onClick={() => navigate(`${preordersPath}/${id}/edit`)} />
          {canReleasePreorder(preorder) && <Button variant="primary" size="display" title={t('PREORDERS_RELEASE')} onClick={() => setReleaseOpen(true)} />}
        </FormActions>
        {operationId && <Note role="status" style={{ marginBottom: 16 }}>
          {t('PREORDERS_RELEASE_STARTED')} {' '}
          <ResourceLink to={`/utilities/operations/${operationId}`}>{t('PREORDERS_VIEW_OPERATION')}</ResourceLink>
        </Note>}
        <DetailGrid>
          <InfoBlock title={t('GENERAL_INFORMATION')} data={[
            { title: t('ID'), value: preorder.id, copy: true },
            { title: t('NAME'), value: preorder.name || '-' },
            { title: t('DEDICATED_USER'), value: preorder.dedicated_user ? `#${preorder.dedicated_user.external_id}` : '-', href: preorder.dedicated_user ? `/dedicated-servers/users/${preorder.dedicated_user.id}` : undefined },
            { title: t('DEDICATED_HOSTNAME'), value: preorder.hostname || '-' },
            { title: t('PREORDERS_OS_ID'), value: preorder.os_id ?? '-' },
            { title: t('PREORDERS_ISSUED_SERVER'), value: preorder.issued_server?.name || preorder.issued_server?.hostname || preorder.issued_server_external_id || '-', href: preorder.issued_server ? `/dedicated-servers/servers/${preorder.issued_server.id}` : undefined },
          ]} />
          <InfoBlock title={t('DEDICATED_CONFIGURATION')} data={[
            { title: 'CPU', value: preorder.cpu ? `${preorder.cpu_count} × ${preorder.cpu.name} · ${preorder.cpu.model}` : '-', isLong: true },
            { title: t('DEDICATED_CPU_CORES'), value: preorder.cpu?.cores ?? '-' },
            { title: t('DEDICATED_CPU_THREADS'), value: preorder.cpu?.threads ?? '-' },
            { title: t('PREORDER_SPEED_MHZ'), value: preorder.cpu?.speed_mhz ?? '-' },
            { title: t('PREORDER_TURBO_SPEED_MHZ'), value: preorder.cpu?.turbo_speed_mhz ?? '-' },
            { title: t('RAM'), value: preorder.ram ? `${preorder.ram_count} × ${preorder.ram.name} · ${preorder.ram.size_mb} MB` : '-', isLong: true },
            { title: t('DISK'), custom: <Configuration>{diskDescriptions(preorder).map((disk, index) => <span key={index}>{disk}</span>)}{!diskDescriptions(preorder).length && '-'}</Configuration> },
          ]} />
          <InfoBlock title={t('PREORDERS_COMMENTS')} data={[
            { title: t('PREORDERS_CUSTOMER_COMMENT'), custom: <Note>{preorder.comment || '-'}</Note> },
            { title: t('PREORDERS_STAFF_COMMENT'), custom: <Note>{preorder.staff_comment || '-'}</Note> },
          ]} />
          <InfoBlock title={t('PREORDERS_TIMELINE')} data={[
            { title: t('CREATED_AT'), value: formatDate(preorder.created_at) },
            { title: t('UPDATED_AT'), value: formatDate(preorder.updated_at) },
            ...(['submitted', 'provisioning', 'released', 'cancelled'] as const).map((status) => ({
              title: t(preorderStatusKey(status)), value: formatDate(preorder[`${status}_at`]),
            })),
          ]} />
          <InfoBlock title={t('SSH_KEYS')} data={[]} customAddComponent={
            <Note>{preorder.ssh_keys == null ? '-' : typeof preorder.ssh_keys === 'string' ? preorder.ssh_keys : JSON.stringify(preorder.ssh_keys, null, 2)}</Note>
          } />
        </DetailGrid>
        {releaseOpen && <ReleaseDedicatedPreorder preorder={preorder} onClose={() => setReleaseOpen(false)} onReleased={(id) => {
          setReleaseOpen(false)
          setOperationId(id)
          viewAlert({ severity: 'information', message: t('PREORDERS_RELEASE_STARTED') })
          reload()
        }} />}
      </>}
    </DetailContainer>
  )
}

const DedicatedPreorderDetails: FC = () => {
  const { preorderId = '' } = useParams()
  return <Details key={preorderId} id={preorderId} />
}

export default DedicatedPreorderDetails
